import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import useTranslation from 'next-translate/useTranslation';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import Skeleton from 'react-loading-skeleton';

import { ArrowLeftNew, ArrowRightNew } from '@/icons/index';
import { Button } from '@/ui/index';

import styles from './news.module.scss';

const NewsList = ({ data }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const [activeTab, setActiveTab] = React.useState(data?.items?.[0]);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  useEffect(() => {
    setActiveTab(data?.items?.[activeIndex]);
  }, [router.locale]);

  const nextHandler = () => {
    if (activeIndex !== data?.items?.length - 1) {
      setIsImageLoaded(false);
      setActiveIndex(activeIndex + 1);
      setActiveTab(data?.items[activeIndex + 1]);
    }
  };

  const prevHandler = () => {
    if (activeIndex !== 0) {
      setIsImageLoaded(false);
      setActiveIndex(activeIndex - 1);
      setActiveTab(data?.items[activeIndex - 1]);
    }
  };

  return (
    <section
      className='section wrapper'
      itemScope
      itemType='https://schema.org/NewsArticle'
    >
      <div className={styles.news_slider}>
        {!isImageLoaded && (
          <Skeleton
            containerClassName={styles.skeleton}
            className={styles.skeletonInner}
            width={'100%'}
            height={'669px'}
          />
        )}
        <div
          style={{
            visibility: isImageLoaded ? 'visible' : 'hidden',
            width: !isImageLoaded && '0%',
          }}
          className={styles.imageWrapper}
        >
          {!!activeTab && activeTab?.homepage_image && (
            <Image
              className={styles.image}
              src={activeTab?.homepage_image}
              width={669}
              height={668}
              alt={activeTab?.title}
              title={activeTab?.title}
              loading='lazy'
              objectFit='cover'
              data-testid={'news-image-id'}
              onLoadingComplete={() => setIsImageLoaded(true)}
            />
          )}
        </div>
        <div className={styles.slider}>
          <div className={styles.content_box}>
            <div className={styles.top}>
              <p className={styles.title}>{data?.title}</p>
              <span className={styles.content_title}> {activeTab?.title}</span>
              <span className={styles.content_description}>
                {activeTab?.description}
              </span>
              <Button
                variant='transparent'
                Component={'router'}
                href={`news/${activeTab?.slug}`}
                className={styles.read_more}
                data-testid={'news-link-id'}
              >
                {t('common:ReadArticle')}
                <ArrowRightNew />
              </Button>
            </div>
            <div className={styles.buttons}>
              <div className={styles.buttonWrapper}>
                <div
                  onClick={() => prevHandler()}
                  className={classNames([
                    styles.previous,
                    {
                      [styles.disabled]: activeIndex + 1 === 1,
                    },
                  ])}
                  data-testid={'news-prev'}
                >
                  <ArrowLeftNew />
                </div>
                <div
                  onClick={() => nextHandler()}
                  className={classNames([
                    styles.next,
                    {
                      [styles.disabled]:
                        activeIndex + 1 === data?.items?.length,
                    },
                  ])}
                  data-testid={'news-next'}
                >
                  <ArrowRightNew />
                </div>
              </div>
              <div className={styles.query}>
                {t('common:PagingWithNumbers', {
                  count: data?.items?.length,
                  current: activeIndex + 1,
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsList;
